import { X } from "lucide-react";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import type { SummaryCaptureValues } from "@/types/voice/summary-capture";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InlineCaptureMomentProps {
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string | null;
  /** The resource this moment was armed by, if the Genie just surfaced one — names the ask. */
  resourceLabel?: string | null;
  /** The Genie asked for the visitor's details on buying intent: a contact ask with name and phone. */
  contactRequest?: boolean;
  values: SummaryCaptureValues;
  onChange: (patch: Partial<SummaryCaptureValues>) => void;
  onSubmit: (e?: React.FormEvent) => void;
  isSubmitting: boolean;
  submitted: boolean;
  error?: string | null;
  onDismiss: () => void;
  /** Set when this visitor already left their email elsewhere — quiet notice instead of the ask. */
  alreadyCapturedEmail?: string | null;
  onChangeEmail?: () => void;
}

/**
 * The in-conversation capture moment — a genie-voiced ask that condenses into
 * the conversation stream itself once a chat feels engaged, instead of a modal
 * interrupting it. One email field, one send button; dismissible, and never
 * shown twice in the same conversation (see useInConversationCapture).
 */
export const InlineCaptureMoment = ({
  brandColor,
  secondaryColor,
  agentName,
  resourceLabel,
  contactRequest = false,
  values,
  onChange,
  onSubmit,
  isSubmitting,
  submitted,
  error,
  onDismiss,
  alreadyCapturedEmail,
  onChangeEmail,
}: InlineCaptureMomentProps) => {
  const end = secondaryColor || "#FF4DB8";
  const showAsk = !submitted && !alreadyCapturedEmail;

  return (
    <div className="flex items-start gap-3">
      <span
        className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl"
        style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
      >
        <GenieIcon name="genie-lamp" size={16} color={brandColor} secondaryColor={end} gradientEnabled />
      </span>
      <div className="min-w-0 flex-1">
        {!submitted && (
          <div className="mb-1 flex items-baseline gap-1.5">
            <span className="text-[11px] font-medium" style={{ color: brandColor }}>
              {agentName || "Genie"}
            </span>
          </div>
        )}
        {/* Kept small on purpose: it sits inside the conversation, so it is one
            line of ask and one row of email + button, not a panel. */}
        <div
          className="relative max-w-[420px] rounded-2xl border border-[#E9EDF3] bg-white p-3 shadow-[0_10px_28px_-20px_rgba(15,23,42,0.35)]"
        >
          {showAsk && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="absolute right-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-black/[0.04] hover:text-[#222B45]"
            >
              <X size={13} strokeWidth={2.25} />
            </button>
          )}
          {showAsk && (
            <p className="mb-2 pr-6 text-[13px] leading-snug text-[#3D4759]">
              {contactRequest
                ? "Leave your details and the team will get back to you."
                : resourceLabel
                  ? `Want me to email you the ${resourceLabel}?`
                  : "Want me to email you this?"}
            </p>
          )}
          <SummaryCaptureForm
            brandColor={brandColor}
            secondaryColor={end}
            values={values}
            onChange={onChange}
            expanded={contactRequest}
            onToggleExpanded={() => {}}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            submitted={submitted}
            error={error}
            hideHeader
            hideExpandToggle
            compact
            autoFocus={false}
            submitLabel={contactRequest ? "Send my details" : "Email me"}
            footnote={null}
            confirmationTitle={contactRequest ? "✓ Details sent" : "✓ Recap on its way"}
            confirmationBody={
              contactRequest
                ? "The team will be in touch soon."
                : resourceLabel
                  ? `We'll email you the ${resourceLabel}.`
                  : "We'll email you a recap of this conversation."
            }
            alreadyCapturedEmail={alreadyCapturedEmail}
            onChangeEmail={onChangeEmail}
          />
        </div>
      </div>
    </div>
  );
};

export default InlineCaptureMoment;
